import {
  createSelector,
  createSlice,
  type Draft,
  type PayloadAction,
} from '@reduxjs/toolkit';

import type { VisualizationData } from '../structuredQuery/types';
import { applyActionResultsReducer } from './action-result-reducers';
import type {
  AISidekickChatState,
  Conversation,
  ConversationMetadata,
  ErrorEvent,
  FeedbackPayload,
  Message,
  PendingHostUserMessage,
  PendingStop,
  RetryAttempt,
  SidekickError,
  StateWithSidekickChat,
  ToolActivity,
} from './chatTypes';
import {
  adoptConversationBranch,
  createConversationBranchReducer,
  discardConversationBranchReducer,
  switchConversationBranchReducer,
} from './conversation-branch-reducers';
import { createEmptyConversationBranchSession } from './conversation-branch-types';

const initialState: AISidekickChatState = {
  conversationId: null,
  conversationScopeVersion: 0,
  conversationMetadata: null,
  messages: [],
  branchSession: createEmptyConversationBranchSession(),
  isStreaming: false,
  isComposerEngaged: false,
  isReconnecting: false,
  streamingContent: '',
  currentToolActivity: null,
  submittedFeedback: {},
  currentTraceId: null,
  error: null,
  retryAttempt: 0,
  errorEvents: [],
  retryingErrorId: null,
  pendingHostUserMessage: null,
  pendingStop: null,
  answeringQuestionId: null,
};

/** Marks a question as sent again, so saved-turn lookups treat it with care. */
const markRetried = (state: AISidekickChatState, questionId: string): void => {
  const question = state.messages.find(({ id }) => id === questionId);
  if (question !== undefined) {
    question.wasRetried = true;
  }
};

const clearConversationBranchSession = (
  state: Draft<AISidekickChatState>
): void => {
  state.branchSession.rootConversationId = null;
  state.branchSession.activeConversationId = null;
  state.branchSession.conversationOrder = [];
  state.branchSession.branchesByConversationId = {};
};

const warnIgnoredVisualizationAttach = (
  messageId: string,
  reason: string
): void => {
  if (import.meta.env.DEV) {
    console.warn('[aiSidekickChat] ignored late visualization attach', {
      messageId,
      reason,
    });
  }
};

/** Redux slice for the AI Sidekick chat domain. */
export const aiSidekickChatSlice = createSlice({
  name: 'aiSidekickChat',
  initialState,
  reducers: {
    /** Wipes in-flight chat data; preserves `submittedFeedback`. */
    resetChat: (state) => {
      state.conversationId = null;
      state.conversationScopeVersion += 1;
      state.conversationMetadata = null;
      state.messages = [];
      clearConversationBranchSession(state);
      state.isStreaming = false;
      state.isComposerEngaged = false;
      state.isReconnecting = false;
      state.streamingContent = '';
      state.currentToolActivity = null;
      state.currentTraceId = null;
      state.error = null;
      state.retryAttempt = 0;
      state.errorEvents = [];
      state.retryingErrorId = null;
      state.pendingHostUserMessage = null;
      state.pendingStop = null;
      state.answeringQuestionId = null;
    },
    /** Marks a question as sent again as a retry. */
    markQuestionRetried: (state, action: PayloadAction<string>) => {
      markRetried(state, action.payload);
    },
    /** Records which local question the running turn answers. */
    setAnsweringQuestion: (state, action: PayloadAction<string | null>) => {
      state.answeringQuestionId = action.payload;
    },
    /** Records a stop that is on its way to the backend. */
    startPendingStop: (state, action: PayloadAction<PendingStop>) => {
      state.pendingStop = action.payload;
    },
    /** Clears a stop once it settles, unless a newer stop replaced it. */
    finishPendingStop: (state, action: PayloadAction<string>) => {
      if (state.pendingStop?.id === action.payload) {
        state.pendingStop = null;
      }
    },
    /** Sets or clears the latest Sidekick chat error. */
    setChatError: (state, action: PayloadAction<SidekickError | null>) => {
      state.error = action.payload;
    },
    /** Appends a single message to the conversation. */
    addMessage: (state, action: PayloadAction<Message>) => {
      // `Message['visualization']` carries `ReadonlyArray<TableColumn>`
      // and `ReadonlyArray<TableRow>` via `TableData`. Immer's
      // `WritableDraft<T>` recursion can't strip those `readonly`
      // modifiers, so we cast at the boundary; the reducer never
      // mutates the visualization payload itself (it only appends).
      const message =
        action.payload.role === 'assistant' &&
        action.payload.sourceQuestionId === undefined &&
        state.answeringQuestionId !== null
          ? { ...action.payload, sourceQuestionId: state.answeringQuestionId }
          : action.payload;
      state.messages.push(message as Draft<Message>);
      if (state.conversationMetadata !== null) {
        state.conversationMetadata.messageCount += 1;
      }
    },
    /**
     * Attaches a late SSE visualization payload to an already-finalized
     * assistant message. Idempotent so duplicate visualization frames cannot
     * overwrite history or affect the conversation message count.
     */
    attachVisualizationToMessage: (
      state,
      action: PayloadAction<{
        messageId: string;
        visualization: VisualizationData;
      }>
    ) => {
      const targetMessage = state.messages.find(
        (message) => message.id === action.payload.messageId
      );
      if (targetMessage === undefined) {
        warnIgnoredVisualizationAttach(action.payload.messageId, 'missing');
        return;
      }
      if (targetMessage.role !== 'assistant') {
        warnIgnoredVisualizationAttach(
          action.payload.messageId,
          'non-assistant'
        );
        return;
      }
      if (targetMessage.visualization !== undefined) {
        warnIgnoredVisualizationAttach(
          action.payload.messageId,
          'already-populated'
        );
        return;
      }
      // See `addMessage` cast rationale — Immer cannot fully unwrap the
      // readonly table payload nested under `VisualizationData`.
      targetMessage.visualization = action.payload
        .visualization as Draft<VisualizationData>;
    },
    /**
     * Records (or overwrites) the feedback rating for a single
     * assistant message. The reducer is intentionally last-write-wins;
     * the toggle / mutual-exclusion behavior between thumbs-up and
     * thumbs-down is enforced at the dispatch site (the feedback row
     * checks the current rating and either dispatches `setFeedback`
     * with the opposite rating or `clearFeedback` to return to
     * neutral).
     */
    setFeedback: (state, action: PayloadAction<FeedbackPayload>) => {
      state.submittedFeedback[action.payload.messageId] = action.payload;
    },
    /**
     * Removes the feedback rating for a single message, returning it
     * to the neutral state. No-op if no rating is stored for the
     * given `messageId`.
     */
    clearFeedback: (state, action: PayloadAction<{ messageId: string }>) => {
      delete state.submittedFeedback[action.payload.messageId];
    },
    /** Replace the current conversation with the provided one. */
    setConversation: (state, action: PayloadAction<Conversation>) => {
      const {
        conversationId,
        messages,
        messageCount,
        createdAt,
        updatedAt,
        metadata,
      } = action.payload;
      let displayMessages = messages;
      const targetBranch =
        state.branchSession.branchesByConversationId[conversationId];
      const isChangingConversation = state.conversationId !== conversationId;
      if (targetBranch === undefined) {
        clearConversationBranchSession(state);
        if (isChangingConversation) {
          if (state.conversationId !== null) {
            state.conversationScopeVersion += 1;
          }
          state.errorEvents = [];
          state.retryAttempt = 0;
          state.retryingErrorId = null;
        }
      } else {
        if (isChangingConversation) {
          if (!adoptConversationBranch(state, targetBranch)) {
            return;
          }
        }
        const displayPrefix = targetBranch.displayMessages.slice(
          0,
          targetBranch.displayAncestorCount
        );
        const conversationMessages = messages.filter(
          (message) => message.conversationId === conversationId
        );
        displayMessages = [...displayPrefix, ...conversationMessages];
        targetBranch.conversation = {
          ...action.payload,
          messages: conversationMessages,
        } as Draft<Conversation>;
        targetBranch.displayMessages = displayMessages as Draft<Message>[];
        state.branchSession.activeConversationId = conversationId;
      }
      state.conversationId = conversationId;
      // See `addMessage` cast rationale — same Immer `readonly` issue.
      state.messages = displayMessages as Draft<Message>[];
      state.conversationMetadata = {
        messageCount,
        createdAt,
        updatedAt,
        ...(metadata !== undefined ? { metadata } : {}),
      };
    },
    /**
     * Sets the current retry attempt counter (0–3). `0` is the idle
     * state and hides the attempt counter on the Thinking indicator.
     */
    setRetryAttempt: (state, action: PayloadAction<RetryAttempt>) => {
      state.retryAttempt = action.payload;
      // An automatic retry resends the running turn's question, which the
      // backend may then save twice.
      if (action.payload > 1 && state.answeringQuestionId !== null) {
        markRetried(state, state.answeringQuestionId);
      }
    },
    /**
     * Appends a new inline `ErrorEvent` to the conversation timeline.
     * Events live alongside `messages`, sorted chronologically by
     * `createdAt`, until a retry resolves or replaces them.
     */
    addErrorEvent: (state, action: PayloadAction<ErrorEvent>) => {
      state.errorEvents.push(action.payload);
    },
    /**
     * Replaces an existing inline error after its retry fails. The
     * event id and timeline position stay stable while the source turn
     * and classification advance to the latest failed attempt.
     */
    replaceErrorEvent: (state, action: PayloadAction<ErrorEvent>) => {
      const targetIndex = state.errorEvents.findIndex(
        (event) => event.id === action.payload.id
      );
      if (targetIndex !== -1) {
        state.errorEvents[targetIndex] = {
          ...(action.payload as Draft<ErrorEvent>),
          createdAt: state.errorEvents[targetIndex].createdAt,
        };
      }
    },
    /** Removes an inline error once its retry succeeds. */
    removeErrorEvent: (state, action: PayloadAction<string>) => {
      const targetIndex = state.errorEvents.findIndex(
        (event) => event.id === action.payload
      );
      if (targetIndex !== -1) {
        state.errorEvents.splice(targetIndex, 1);
      }
    },
    /**
     * Records the id of the `ErrorEvent` whose Retry button is
     * currently mid-attempt, or `null` to indicate no retry is in
     * flight. Drives the per-card icon rotation in `AgentErrorCard`.
     */
    setRetryingErrorId: (state, action: PayloadAction<string | null>) => {
      state.retryingErrorId = action.payload;
    },
    /**
     * Stores the host-originated suggestion text until `ChatInterface`
     * can submit it through `useConversation.sendMessage`.
     */
    setPendingHostUserMessage: (
      state,
      action: PayloadAction<PendingHostUserMessage | null>
    ) => {
      if (action.payload !== null && state.pendingHostUserMessage !== null) {
        return;
      }
      state.pendingHostUserMessage = action.payload;
    },
    /**
     * Clears a consumed host-originated suggestion request. The id
     * guard prevents an older consumer render from clearing a newer
     * request if the user clicks chips rapidly.
     */
    clearPendingHostUserMessage: (state, action: PayloadAction<string>) => {
      if (state.pendingHostUserMessage?.id === action.payload) {
        state.pendingHostUserMessage = null;
      }
    },
    /**
     * Clears the in-flight retry surface (`retryAttempt` +
     * `retryingErrorId`). Called when a retry resolves, fails, or
     * unmounts.
     *
     * Error-event replacement or removal is handled separately based
     * on whether the retry failed or succeeded.
     */
    clearRetryState: (state) => {
      state.retryAttempt = 0;
      state.retryingErrorId = null;
    },
    /**
     * Sets the streaming flag for the in-flight assistant turn. Dispatched
     * by the SSE event-stream hook (`useSidekickEventStream`) when the
     * connection opens (`true`) and after the `done` frame fires
     * (`false`). The synchronous POST fallback hook
     * (`useSidekickSyncResponse`) also toggles it. When set to `false`,
     * also clears `isReconnecting` so the indicator cannot remain stuck
     * after `done`, abort, or transport failure.
     */
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
      if (!action.payload) {
        state.isReconnecting = false;
      }
    },
    /**
     * Publishes whether the composer input is engaged (focused or
     * holding typed content). Dispatched by `InputBar` so the
     * entry-point card's brand mark can mirror the in-chat icon's
     * `listening` state. Reset to `false` when the composer unmounts.
     */
    setComposerEngaged: (state, action: PayloadAction<boolean>) => {
      state.isComposerEngaged = action.payload;
    },
    /**
     * Toggles the mid-stream reconnect indicator (`lastEventId` query-param
     * retry). Dispatched by `useSidekickEventStream` before a mid-stream
     * reopen (`true`) and cleared on the first data event after reconnect
     * or when streaming ends.
     */
    setIsReconnecting: (state, action: PayloadAction<boolean>) => {
      state.isReconnecting = action.payload;
    },
    /**
     * Reserved buffer for a future incremental-rendering path. The
     * current SSE UX intentionally buffers `textDelta` outside Redux
     * until `messageDone` identifies user-facing content.
     */
    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.streamingContent += action.payload;
    },
    /**
     * Resets the streaming buffer to the empty string. Called after the
     * accumulated buffer is flushed into a finalized `Message` (on
     * `messageDone`) or when the streaming surface is torn down
     * (unmount, conversation change, panel close).
     */
    clearStreamingContent: (state) => {
      state.streamingContent = '';
    },
    /**
     * Records the in-flight tool activity reported by the BE (or clears
     * it with `null` on idle / completion). Drives the Thinking
     * indicator label + animation per Figma. Wire shape matches the
     * `conversations.proto` `ToolActivity` so the SSE hook can dispatch
     * the payload verbatim.
     */
    setToolActivity: (state, action: PayloadAction<ToolActivity | null>) => {
      state.currentToolActivity = action.payload;
    },
    /**
     * Stores (or clears with `null`) the trace id returned by the BE
     * on the `done` SSE frame (or in the sync POST response). Consumed
     * by the feedback row (SDPCDXP-1634) to correlate user feedback
     * with the assistant turn that produced it.
     */
    setCurrentTraceId: (state, action: PayloadAction<string | null>) => {
      state.currentTraceId = action.payload;
    },
    /**
     * Attaches the terminal response trace id to the latest assistant
     * message so per-message feedback can correlate to the correct MLflow
     * trace. No-ops when the response does not include a usable trace id.
     */
    attachTraceIdToLatestAssistantMessage: (
      state,
      action: PayloadAction<string>
    ) => {
      const traceId = action.payload;
      if (traceId === '') {
        return;
      }
      for (let index = state.messages.length - 1; index >= 0; index -= 1) {
        const message = state.messages[index];
        if (message.role === 'assistant') {
          message.traceId = traceId;
          return;
        }
      }
    },
    /**
     * Sets (or clears with `null`) just the active conversation id —
     * used when the orchestrator establishes a new conversation before
     * the full `Conversation` payload is available. `setConversation`
     * remains the action for replacing the full hydrated conversation.
     */
    setConversationId: (state, action: PayloadAction<string | null>) => {
      state.conversationId = action.payload;
    },
    createConversationBranch: createConversationBranchReducer,
    discardConversationBranch: discardConversationBranchReducer,
    switchConversationBranch: switchConversationBranchReducer,
    applyActionResults: applyActionResultsReducer,
  },
});

export const {
  resetChat,
  markQuestionRetried,
  setAnsweringQuestion,
  startPendingStop,
  finishPendingStop,
  setChatError,
  addMessage,
  attachVisualizationToMessage,
  setFeedback,
  clearFeedback,
  setConversation,
  setRetryAttempt,
  addErrorEvent,
  replaceErrorEvent,
  removeErrorEvent,
  setRetryingErrorId,
  setPendingHostUserMessage,
  clearPendingHostUserMessage,
  clearRetryState,
  setIsStreaming,
  setComposerEngaged,
  setIsReconnecting,
  appendStreamingContent,
  clearStreamingContent,
  setToolActivity,
  setCurrentTraceId,
  attachTraceIdToLatestAssistantMessage,
  setConversationId,
  createConversationBranch,
  discardConversationBranch,
  switchConversationBranch,
  applyActionResults,
} = aiSidekickChatSlice.actions;

/** Selects the ordered list of messages in the current conversation. */
export const selectMessages = (state: StateWithSidekickChat) =>
  state.aiSidekickChat.messages;

/** Selects the active conversation id (null when no conversation has started). */
export const selectConversationId = (
  state: StateWithSidekickChat
): string | null => state.aiSidekickChat.conversationId;

/** Selects the logical conversation scope version advanced by chat resets. */
export const selectConversationScopeVersion = (
  state: StateWithSidekickChat
): number => state.aiSidekickChat.conversationScopeVersion;

/**
 * Selects whether the assistant is currently streaming a response.
 *
 * Defensive against an absent slice: the entry-point card calls this
 * (via `useSidekickIconState`) before its feature-flag early-return, so
 * it can run in compositions that mount the package UI without
 * `aiSidekickChatReducer`. The input type is widened to
 * `Partial<StateWithSidekickChat>` to reflect that supported shape, and
 * it defaults to `false` rather than throwing.
 */
export const selectIsStreaming = (
  state: Partial<StateWithSidekickChat>
): boolean => state.aiSidekickChat?.isStreaming ?? false;

/**
 * Selects whether the composer input is currently engaged (focused or
 * holding typed content). Drives the entry-point card's `listening`
 * brand-mark state so it stays in sync with the in-chat icon.
 *
 * Defensive against an absent slice for the same reason as
 * {@link selectIsStreaming}; widened input type and defaults to `false`.
 */
export const selectIsComposerEngaged = (
  state: Partial<StateWithSidekickChat>
): boolean => state.aiSidekickChat?.isComposerEngaged ?? false;

/**
 * Selects whether a mid-stream SSE reconnect is in flight (`lastEventId`
 * query-param retry). Drives the reconnecting label on the Thinking indicator.
 */
export const selectIsReconnecting = (state: StateWithSidekickChat): boolean =>
  state.aiSidekickChat.isReconnecting;

/** Selects the latest Sidekick chat error, or null when there is none. */
export const selectSidekickError = (
  state: StateWithSidekickChat
): SidekickError | null => state.aiSidekickChat.error;

/** Selects the active trace id for telemetry correlation. */
export const selectCurrentTraceId = (
  state: StateWithSidekickChat
): string | null => state.aiSidekickChat.currentTraceId;

/** Selects the in-flight tool activity reported by the backend, or null when idle. */
export const selectCurrentToolActivity = (state: StateWithSidekickChat) =>
  state.aiSidekickChat.currentToolActivity;

/** Selects the map of submitted feedback payloads keyed by `messageId`. */
export const selectSubmittedFeedback = (state: StateWithSidekickChat) =>
  state.aiSidekickChat.submittedFeedback;

/** Selects the reserved streaming content buffer; intentionally unused today. */
export const selectStreamingContent = (state: StateWithSidekickChat): string =>
  state.aiSidekickChat.streamingContent;

/** Selects the current retry attempt counter (`0`–`3`). */
export const selectRetryAttempt = (
  state: StateWithSidekickChat
): RetryAttempt => state.aiSidekickChat.retryAttempt;

/**
 * Selects the chronologically-ordered list of inline error events
 * surfaced in the chat timeline. Each entry renders one
 * `AgentErrorCard`.
 */
export const selectErrorEvents = (state: StateWithSidekickChat): ErrorEvent[] =>
  state.aiSidekickChat.errorEvents;

/**
 * Selects the id of the `ErrorEvent` whose Retry button is currently
 * mid-attempt, or `null` when no retry is in flight.
 */
export const selectRetryingErrorId = (
  state: StateWithSidekickChat
): string | null => state.aiSidekickChat.retryingErrorId;

/** Selects the pending host-originated user message, if one exists. */
export const selectPendingHostUserMessage = (
  state: StateWithSidekickChat
): PendingHostUserMessage | null => state.aiSidekickChat.pendingHostUserMessage;

/** Whether a stop for the current conversation is still pending. */
export const selectIsStopPending = (state: StateWithSidekickChat): boolean => {
  const { pendingStop, conversationId } = state.aiSidekickChat;
  return pendingStop !== null && pendingStop.conversationId === conversationId;
};

/** Selects the chat slice's stored conversation metadata blob, or null. */
export const selectConversationMetadata = (
  state: StateWithSidekickChat
): ConversationMetadata | null => state.aiSidekickChat.conversationMetadata;

/** Selects the active conversation, or null when not yet hydrated. */
export const selectConversation = createSelector(
  [selectConversationId, selectConversationMetadata, selectMessages],
  (conversationId, conversationMetadata, messages): Conversation | null => {
    if (conversationId === null || conversationMetadata === null) {
      return null;
    }
    const { messageCount, createdAt, updatedAt, metadata } =
      conversationMetadata;
    return {
      conversationId,
      messageCount,
      createdAt,
      updatedAt,
      messages: messages.filter(
        (message) => message.conversationId === conversationId
      ),
      ...(metadata !== undefined ? { metadata } : {}),
    };
  }
);

export default aiSidekickChatSlice.reducer;
